/**
 * Indonesian Address Service
 * Uses backoffice-service /v1/geographic/* endpoints.
 * Hierarchy: Province -> City (Kota/Kabupaten) -> District (Kecamatan) -> Subdistrict (Kelurahan/Desa)
 *
 * asyncFunction contract for MyAsyncDropdown:
 *   receives: { search: string, ...extraData }
 *   returns:  Promise<{ loading: boolean, data: Array }>
 */

import { get } from '@src/services/NetworkUtils'

async function fetchList(endpoint, params = {}) {
  const res = await get(endpoint, params)
  return res?.data ?? []
}

/**
 * Search provinces.
 *
 * @param {{ search: string }} params
 * @returns {Promise<{ loading: boolean, data: Array }>}
 */
export async function searchProvinces({ search = '' } = {}) {
  try {
    const data = await fetchList('/v1/geographic/provinces', { search })
    return { loading: false, data }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[searchProvinces]', err)
    return { loading: false, data: [] }
  }
}

/**
 * Search CLIK region cities (kota/kabupaten) from the clik_domain_codes table.
 * Unlike searchKota (geographic cities.json, ids not CLIK codes), each option
 * carries `code` = the CLIK RegionDomain code that maps straight to ktp_city_code
 * → address.city in the bureau payload. Mirrors the cities endpoint the consent
 * flow uses, so HR-supplied and candidate-supplied KTPs produce the same codes.
 *
 * @param {{ search: string }} params
 * @returns {Promise<{ loading: boolean, data: Array<{ id: string, name: string, code: string }> }>}
 */
export async function searchClikCities({ search = '' } = {}) {
  try {
    const data = await fetchList('/v1/geographic/clik-cities', { search })
    return { loading: false, data }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[searchClikCities]', err)
    return { loading: false, data: [] }
  }
}

let allCitiesCache = null

/**
 * Search cities (Kota/Kabupaten).
 * When province_id is provided, queries that province only.
 * Without province_id, loads all cities once (for forms without a province field).
 *
 * @param {{ search: string, province_id?: string }} params
 * @returns {Promise<{ loading: boolean, data: Array }>}
 */
export async function searchKota({ search = '', province_id } = {}) {
  try {
    if (province_id) {
      const data = await fetchList(`/v1/geographic/cities/${province_id}`, { search })
      return { loading: false, data }
    }

    if (!allCitiesCache) {
      const provinces = await fetchList('/v1/geographic/provinces')
      const cityLists = await Promise.all(
        provinces.map((p) => fetchList(`/v1/geographic/cities/${p.id}`))
      )
      allCitiesCache = cityLists.flat()
    }

    const keyword = search?.toLowerCase().trim() ?? ''
    const data = keyword
      ? allCitiesCache.filter((c) => c.name?.toLowerCase().includes(keyword))
      : allCitiesCache

    return { loading: false, data: data.slice(0, 50) }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[searchKota]', err)
    return { loading: false, data: [] }
  }
}

/**
 * Search districts (Kecamatan) by city ID.
 * Accepts city_id or regency_id (legacy alias).
 *
 * @param {{ search: string, city_id?: string, regency_id?: string }} params
 * @returns {Promise<{ loading: boolean, data: Array }>}
 */
export async function searchKecamatan({ search = '', city_id, regency_id } = {}) {
  const id = city_id ?? regency_id
  if (!id) return { loading: false, data: [] }

  try {
    const data = await fetchList(`/v1/geographic/districts/${id}`, { search })
    return { loading: false, data }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[searchKecamatan]', err)
    return { loading: false, data: [] }
  }
}

/**
 * Search subdistricts (Kelurahan/Desa) by district ID.
 *
 * @param {{ search: string, district_id: string }} params
 * @returns {Promise<{ loading: boolean, data: Array }>}
 */
export async function searchKelurahan({ search = '', district_id } = {}) {
  if (!district_id) return { loading: false, data: [] }

  try {
    const data = await fetchList(`/v1/geographic/subdistricts/${district_id}`, { search })
    return { loading: false, data }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[searchKelurahan]', err)
    return { loading: false, data: [] }
  }
}

/**
 * Search postal codes by subdistrict ID.
 *
 * @param {{ search: string, subdistrict_id: string }} params
 * @returns {Promise<{ loading: boolean, data: Array }>}
 */
export async function searchPostalCodes({ search = '', subdistrict_id } = {}) {
  if (!subdistrict_id) return { loading: false, data: [] }

  try {
    const data = await fetchList(`/v1/geographic/postal-codes/${subdistrict_id}`, { search })
    return { loading: false, data }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[searchPostalCodes]', err)
    return { loading: false, data: [] }
  }
}

/**
 * Fetch the first postal code for a subdistrict (used for auto-fill).
 *
 * @param {string} subdistrictId
 * @returns {Promise<string|null>}
 */
export async function fetchDefaultPostalCode(subdistrictId) {
  if (!subdistrictId) return null
  const { data } = await searchPostalCodes({ subdistrict_id: subdistrictId })
  return data[0]?.code ?? null
}
